import { useNodeManager } from '@/composables/useNodeManager';
import {
  MAX_SCALE,
  MAX_SCALE_X,
  MAX_SCALE_Y,
  MAX_SCALE_Z,
  MIN_SCALE,
  MIN_SCALE_X,
  MIN_SCALE_Y,
  MIN_SCALE_Z,
} from '@/constants/appConstant';
import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { camera, cameraControls, render, renderer, scene } from '@/threejs/core';
import { getObjectByCode, updateObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import { watch } from 'vue';
import { TransformControls } from './TransformControls';

export let transformControls;
let isTransformingTarget = false;
let savedProjectorUpVector = null;
let savedLedTileCounts = null;

// Snapping configuration for projector target
const SNAP_ENABLED = true; // Enable/disable snapping
const SNAP_DISTANCE = 5.0; // Maximum distance to snap (in world units)
const snapRaycaster = new THREE.Raycaster();

// Projector target world position locking
// When true (default), moving the projector keeps the target's world position fixed
// and updates the throw distance accordingly
const KEEP_TARGET_WORLD_POSITION = true;

const isScaleDisabledForMesh = (mesh) => {
  return mesh?.userData?.type === 'projector' || mesh?.parent?.userData?.type === 'projector';
};

const safeScale = (val, fallback = 1) => {
  return isNaN(val) || val === null ? fallback : val;
};

const setGizmoDisabled = (disabled) => {
  if (!transformControls) return;
  transformControls.enabled = !disabled;
  const helper = transformControls.getHelper?.();
  if (!helper) return;

  helper.traverse((obj) => {
    if (!obj.material) return;
    if (!obj.userData._origMaterial) {
      obj.userData._origMaterial = obj.material.clone?.() || obj.material;
    }
    if (disabled) {
      if (obj.material.color) obj.material.color.setHex(0x6b7280);
      obj.material.opacity = 0.35;
      obj.material.transparent = true;
    } else if (obj.userData._origMaterial) {
      if (obj.material.color && obj.userData._origMaterial.color) {
        obj.material.color.copy(obj.userData._origMaterial.color);
      }
      if (typeof obj.userData._origMaterial.opacity === 'number') {
        obj.material.opacity = obj.userData._origMaterial.opacity;
      }
    }
  });
};
let localTargetPoint = new THREE.Vector3();

/**
 * Update visibility of all projector and target sprites based on transform mode
 * @param {boolean} hideSprites - Whether to hide sprites (true when in transform mode)
 */
function updateAllSpritesVisibility(hideSprites) {
  // Check global viewport settings to see if sprites are enabled
  const cruxModuleStore = useCruxModuleStore();
  const globalSpritesEnabled = cruxModuleStore.viewportSettingsModule?.data?.['render.showProjectorSprite'] ?? true;

  // Only show sprites if both conditions are met:
  // 1. Transform mode allows it (!hideSprites)
  // 2. Global setting has sprites enabled
  const shouldShowSprites = !hideSprites && globalSpritesEnabled;

  scene.traverse((object) => {
    if (object.userData.type === 'projector' && object.projectorHelpers) {
      object.projectorHelpers.updateProjectorSpriteVisibility(shouldShowSprites);
      object.projectorHelpers.updateTargetSpriteVisibility(shouldShowSprites);
    }
  });
}

function setScaleGizmoMode(mesh, transformControls) {
  if (mesh.userData.lock_scale) {
    transformControls.showX = true;
    transformControls.showY = false;
    transformControls.showZ = false;
    transformControls.axis = 'XYZ';
  } else {
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
  }
}

/**
 * Attempts to snap the target position to a mesh surface near the target
 * Respects the transform control's axis constraints
 * @param {THREE.Vector3} transformPosition - The position calculated by transform controls
 * @param {string} targetCode - The code/uuid of the target being transformed
 * @param {string} axis - The current transform axis ('X', 'Y', 'Z', 'XY', 'XZ', 'YZ', 'XYZ', etc.)
 * @returns {THREE.Vector3} - The snapped position or original if no snap found
 */
function trySnapToSurface(transformPosition, targetCode, axis) {
  if (!SNAP_ENABLED || !axis) return transformPosition;

  // Collect all selectable meshes in the scene (excluding the target itself)
  const snapTargets = [];
  scene.traverse((object) => {
    // Only include meshes that are selectable and not the transform target
    if (
      object.isMesh &&
      object.userData.selectable === true &&
      !object.userData.isTarget &&
      !object.userData.isProjectorTarget &&
      !object.userData.isAnnotationTarget &&
      object.visible
    ) {
      snapTargets.push(object);
    }
  });

  if (snapTargets.length === 0) return transformPosition;

  // Cast ray from camera through the target's current position
  const targetScreenPos = transformPosition.clone().project(camera);
  snapRaycaster.setFromCamera(targetScreenPos, camera);
  snapRaycaster.far = 200.0; // Cast far to find surfaces

  const intersects = snapRaycaster.intersectObjects(snapTargets, true);

  // If we hit a surface along the ray through the target
  if (intersects.length > 0) {
    const hitPoint = intersects[0].point;

    // Check if hit point is within snap distance
    const distance = transformPosition.distanceTo(hitPoint);

    if (distance < SNAP_DISTANCE) {
      // Calculate the snapped position based on active axes
      const snappedPosition = transformPosition.clone();

      // Only snap along the active axes
      const activeX = axis.includes('X');
      const activeY = axis.includes('Y');
      const activeZ = axis.includes('Z');

      // Snap to hit point along active axes
      if (activeX) {
        snappedPosition.x = hitPoint.x;
      }
      if (activeY) {
        snappedPosition.y = hitPoint.y;
      }
      if (activeZ) {
        snappedPosition.z = hitPoint.z;
      }

      return snappedPosition;
    }
  }

  return transformPosition;
}

function updateScale(object, payload, isFinal = false) {
  // Clamp scale for all objects
  if (object.userData.lock_scale === true) {
    const uniformScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, safeScale(object.scale.x)));
    object.scale.set(uniformScale, uniformScale, uniformScale);
    payload.scale = uniformScale;
  } else {
    object.scale.x = Math.max(MIN_SCALE_X, Math.min(MAX_SCALE_X, safeScale(object.scale.x)));
    object.scale.y = Math.max(MIN_SCALE_Y, Math.min(MAX_SCALE_Y, safeScale(object.scale.y)));
    object.scale.z = Math.max(MIN_SCALE_Z, Math.min(MAX_SCALE_Z, safeScale(object.scale.z)));
    payload.scale = { x: object.scale.x, y: object.scale.y, z: object.scale.z };
  }

  // LED-specific: convert scale to tile counts
  if (object.userData.type === 'led_screen' && savedLedTileCounts) {
    const scaleX = safeScale(object.scale.x);
    const scaleY = safeScale(object.scale.y);

    const origH = savedLedTileCounts.horizontalSize;
    const origV = savedLedTileCounts.verticalSize;
    const tileWidth = origH > 0 ? savedLedTileCounts.totalWidth / origH : 0;
    const tileHeight = origV > 0 ? savedLedTileCounts.totalHeight / origV : 0;

    if (tileWidth > 0 && tileHeight > 0) {
      const newH = Math.max(1, Math.round(origH * scaleX));
      const newV = Math.max(1, Math.round(origV * scaleY));

      payload.horizontalSize = newH;
      payload.verticalSize = newV;
      payload.totalWidth = tileWidth * newH;
      payload.totalHeight = tileHeight * newV;
    }

    // On mouseUp: reset LED scale and clear saved state
    if (isFinal) {
      object.scale.set(1, 1, 1);
      payload.scale = { x: 1, y: 1, z: 1 };
      savedLedTileCounts = null;
    }
  }
}
export function setUpTransformControls(sceneParam, cameraParam, cameraControlsParam) {
  const selectionStore = useSelectionStore();
  const { updateNode } = useNodeManager();
  const cruxModuleStore = useCruxModuleStore();

  // Use passed camera or fall back to imported camera
  const activeCamera = cameraParam || camera;
  transformControls = new TransformControls(activeCamera, renderer.domElement);
  transformControls.size = 0.75;
  transformControls.getRaycaster().layers.set(1);
  transformControls.addEventListener('change', function (event) {
    render();
  });

  transformControls.addEventListener('dragging-changed', function (event) {
    cameraControls.enabled = !event.value;
  });

  transformControls.addEventListener('mouseDown', function (event) {
    cruxModuleStore.pause();

    transformControls.object.isTransforming = true;

    // Save LED tile counts at drag start for live scaling
    if (transformControls.object.userData.type === 'led_screen') {
      const ud = transformControls.object.userData;
      savedLedTileCounts = {
        horizontalSize: ud.horizontalSize || 1,
        verticalSize: ud.verticalSize || 1,
        totalWidth: ud.totalWidth || 0,
        totalHeight: ud.totalHeight || 0,
      };
    }

    if (transformControls.object.userData.isProjectorTarget) {
      const projectorObject = getObjectByCode(transformControls.object.userData.projectorCode);

      projectorObject.isTransforming = true;
    }

    if (transformControls.object && transformControls.object.userData.isProjector) {
      transformControls.object.updateMatrixWorld(true);

      localTargetPoint = transformControls.object.userData.target.clone();
      transformControls.object.worldToLocal(localTargetPoint);

      // Save the world-space up vector to preserve roll during drag
      savedProjectorUpVector = new THREE.Vector3(0, 1, 0);
      savedProjectorUpVector.applyQuaternion(transformControls.object.quaternion).normalize();
    } else {
      // Clear saved up vector if not transforming a projector
      savedProjectorUpVector = null;
    }

    // Save initial position for groups to compute delta during drag
    if (transformControls.object?.userData?.type === 'group') {
      transformControls.object.userData._prevPos = transformControls.object.position.clone();
    }
  });

  // Listen for camera changes and update RenderPass
  window.addEventListener('cameraChanged', (event) => {
    const newCamera = event.detail.camera;
    transformControls.camera = newCamera;
  });

  transformControls.addEventListener('mouseUp', function (event) {
    const object = transformControls.object;
    object.isTransforming = false;

    cruxModuleStore.resume();
    cruxModuleStore.commit();

    if (object.userData.isProjectorTarget) {
      const updatedWorldPoint = object.position.clone();

      const projectorObject = getObjectByCode(object.userData.projectorCode);
      projectorObject.isTransforming = false;
      updateNode(
        object.userData.projectorCode,
        {
          target: {
            x: updatedWorldPoint.x,
            y: updatedWorldPoint.y,
            z: updatedWorldPoint.z,
          },
        },
        true
      );

      return;
    }

    const updatedWorldPoint = localTargetPoint.clone();
    object.localToWorld(updatedWorldPoint);
    const payload = {
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      },
      rotation: {
        x: object.rotation.x * (180 / Math.PI),
        y: object.rotation.y * (180 / Math.PI),
        z: object.rotation.z * (180 / Math.PI),
      },
    };

    if (transformControls.mode === 'scale') {
      // Check zoom before updateScale resets LED scale to 1
      updateScale(object, payload, object.userData.type === 'led_screen');
    }
    updateNode(object.userData.code, payload, true);

    if (object.userData.isProjector && object.userData.lockTarget !== true) {
      updateNode(
        object.userData.code,
        {
          target: {
            x: updatedWorldPoint.x,
            y: updatedWorldPoint.y,
            z: updatedWorldPoint.z,
          },
        },
        true
      );
    }

    // Clear saved up vector after transform is complete
    savedProjectorUpVector = null;
  });

  transformControls.addEventListener('objectChange', function (event) {
    const object = event.target.object;

    if (object.userData.isProjectorTarget) {
      const projectorObject = getObjectByCode(object.userData.projectorCode);
      projectorObject.updateMatrixWorld(true);
      const updatedWorldPoint = object.position.clone();
      // projectorObject.localToWorld(updatedWorldPoint)
      projectorObject.rotateToTarget(object.position.clone());
      projectorObject.userData.target.copy(updatedWorldPoint.clone());
      updateNode(object.userData.projectorCode, {
        rotation: {
          x: projectorObject.rotation.x * (180 / Math.PI),
          y: projectorObject.rotation.y * (180 / Math.PI),
          z: projectorObject.rotation.z * (180 / Math.PI),
        },
        target: {
          x: updatedWorldPoint.x,
          y: updatedWorldPoint.y,
          z: updatedWorldPoint.z,
        },
      });

      return;
    }
    const updatedWorldPoint = localTargetPoint.clone();
    object.localToWorld(updatedWorldPoint);
    const payload = {
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z,
      },
      rotation: {
        x: object.rotation.x * (180 / Math.PI),
        y: object.rotation.y * (180 / Math.PI),
        z: object.rotation.z * (180 / Math.PI),
      },
    };

    if (transformControls.mode === 'scale') {
      updateScale(object, payload);
    }

    updateNode(object.userData.code, payload, true);

    // For groups: move child projectors' targets with the group during drag
    if (object.userData.type === 'group' && object.userData._prevPos) {
      const delta = new THREE.Vector3().subVectors(object.position, object.userData._prevPos);
      if (delta.lengthSq() > 0) {
        object.traverse((child) => {
          if (child.userData?.isProjector) {
            child.userData.target.add(delta);
            child.rotateToTarget(child.userData.target);
            const targetMesh = getObjectByCode(child.userData.code + '-target');
            if (targetMesh) targetMesh.position.copy(child.userData.target);
            updateNode(child.userData.code, {
              target: { x: child.userData.target.x, y: child.userData.target.y, z: child.userData.target.z },
            });
          }
        });
      }
      object.userData._prevPos = object.position.clone();
    }

    if (object.userData.isProjector && object.userData.lockTarget !== true) {
      updateNode(object.userData.code, {
        target: {
          x: updatedWorldPoint.x,
          y: updatedWorldPoint.y,
          z: updatedWorldPoint.z,
        },
      });
      object.userData.target.copy(updatedWorldPoint.clone());
      const targetMesh = getObjectByCode(object.userData.code + '-target');
      if (targetMesh) {
        targetMesh.position.copy(updatedWorldPoint.clone());
      }
    }

    if (object.userData.isProjector && savedProjectorUpVector) {
      // Restore the saved up vector to preserve roll during translate drag
      const targetPos = object.userData.target.clone();
      const directionToTarget = new THREE.Vector3();
      directionToTarget.subVectors(targetPos, object.position);

      if (directionToTarget.lengthSq() > 0) {
        directionToTarget.normalize();

        // The projector's local -Z axis should point toward the target
        const newLocalZ = directionToTarget.clone().negate();

        // Calculate right vector (local X) using saved up vector
        const newLocalX = new THREE.Vector3();
        newLocalX.crossVectors(savedProjectorUpVector, newLocalZ);

        // Check if up and forward are parallel (or nearly so)
        if (newLocalX.lengthSq() < 0.000001) {
          // Vectors are parallel, use default up
          const defaultUp = new THREE.Vector3(0, 1, 0);
          newLocalX.crossVectors(defaultUp, newLocalZ).normalize();
          savedProjectorUpVector.crossVectors(newLocalZ, newLocalX).normalize();
        } else {
          newLocalX.normalize();
          // Recalculate up to ensure orthogonality (local Y)
          const newLocalY = new THREE.Vector3();
          newLocalY.crossVectors(newLocalZ, newLocalX).normalize();
          savedProjectorUpVector.copy(newLocalY);
        }

        // Build rotation matrix from basis vectors
        const rotationMatrix = new THREE.Matrix4();
        rotationMatrix.makeBasis(newLocalX, savedProjectorUpVector, newLocalZ);

        // Apply the rotation
        object.quaternion.setFromRotationMatrix(rotationMatrix);
      }
    }

    return;

    // Handle projector target2 (draggable gizmo)
    if (object.userData.isProjectorTarget && isTransformingTarget) {
      // Get the current axis from transform controls
      const currentAxis = transformControls.axis || 'XYZ';

      // Apply snapping if enabled and transforming a target
      let snappedPosition = object.position.clone();

      if (transformControls.dragging) {
        // Pass the transform-calculated position and current axis for snapping
        snappedPosition = trySnapToSurface(object.position, object.userData.forUuid, currentAxis);

        // Update object position to snapped position
        object.position.copy(snappedPosition);

        // Get the projector/spotlight object
        const spotLight = getObjectByCode(object.userData.forUuid);
        if (spotLight && spotLight.userData.type == 'projector' && spotLight.target2) {
          // Calculate the direction from spotlight to target2
          const direction = new THREE.Vector3().subVectors(snappedPosition, spotLight.position);

          // Calculate the distance
          const distance = direction.length();

          // Update target position (local to spotlight)
          spotLight.target.position.set(0, 0, -distance);

          // Make spotlight look at target2 position
          spotLight.lookAt(snappedPosition);

          // Copy spotlight's rotation back to target2 so local transform mode stays aligned
          spotLight.target2.quaternion.copy(spotLight.quaternion);

          // Update texture rotation
          if (spotLight.projectorTexture) {
            spotLight.projectorTexture.updateTextureRotation(spotLight.rotation);
          }

          // Update helper lines
          if (spotLight.projectorHelpers) {
            spotLight.projectorHelpers.updateHelperLines();
          }
        }
      }
    } else if (object.userData.isTarget) {
      // Handle other target types (not projector target2)
      // Get the current axis from transform controls
      const currentAxis = transformControls.axis || 'XYZ';

      // Apply snapping if enabled and transforming a target
      let snappedPosition = object.position.clone();

      if (isTransformingTarget && transformControls.dragging) {
        // Pass the transform-calculated position and current axis for snapping
        snappedPosition = trySnapToSurface(object.position, object.userData.forUuid, currentAxis);

        const targetObject = getObjectByCode(object.userData.forUuid);
        targetObject.lookAt(snappedPosition);

        targetObject.applyProperties({
          rotation: {
            x: targetObject.rotation.x * (180 / Math.PI),
            y: targetObject.rotation.y * (180 / Math.PI),
            z: targetObject.rotation.z * (180 / Math.PI),
          },
        });
      }
    } else {
      if (object && object.userData.isProjector) {
        const updatedWorldPoint = localTargetPoint.clone();
        object.localToWorld(updatedWorldPoint);

        updateNode(object.userData.code, {
          position: {
            x: object.position.x,
            y: object.position.y,
            z: object.position.z,
          },
          target: {
            x: updatedWorldPoint.x,
            y: updatedWorldPoint.y,
            z: updatedWorldPoint.z,
          },
          rotation: {
            x: object.rotation.x * (180 / Math.PI),
            y: object.rotation.y * (180 / Math.PI),
            z: object.rotation.z * (180 / Math.PI),
          },
        });
      }

      return;
      // For projectors with world position locking, handle rotation separately
      // Only apply locking when in translate mode, not rotate mode
      const isProjectorWithLocking =
        object.userData.isProjector &&
        object.target2 &&
        object.target &&
        KEEP_TARGET_WORLD_POSITION &&
        transformControls.mode === 'translate';

      // Save target2 position before applyProperties if locking is enabled
      let savedTarget2Position = null;
      if (isProjectorWithLocking) {
        savedTarget2Position = object.target2.position.clone();
      }

      const properties = {
        position: {
          x: object.position.x,
          y: object.position.y,
          z: object.position.z,
        },
      };

      // Only apply rotation if not a projector with world position locking
      if (!isProjectorWithLocking) {
        properties.rotation = {
          x: object.rotation.x * (180 / Math.PI),
          y: object.rotation.y * (180 / Math.PI),
          z: object.rotation.z * (180 / Math.PI),
        };
      }

      object.applyProperties(properties);

      // Restore target2 position after applyProperties if locking is enabled
      if (isProjectorWithLocking && savedTarget2Position) {
        object.target2.position.copy(savedTarget2Position);
      }
      if (object.userData.isProjector) {
        // Update target2 position and rotation when spotlight moves or rotates
        if (object.target2 && object.target) {
          if (KEEP_TARGET_WORLD_POSITION && transformControls.mode === 'translate') {
            // Keep target2's world position fixed, update target's local position
            // and throw distance based on new projector position

            // Calculate direction from projector to fixed target2 position
            const direction = new THREE.Vector3().subVectors(object.target2.position, object.position);
            const distance = direction.length();

            object.userData.lockTarget2 = true;
            // Update target's local position (in spotlight's local space)
            // Normalize direction and apply inverse of spotlight rotation
            direction.normalize();
            const localDirection = direction.clone();

            localDirection.applyQuaternion(object.quaternion.clone().invert());
            //object.target.position.copy(localDirection.multiplyScalar(distance))

            // Make spotlight look at the fixed target2 position
            // object.lookAt(object.target2.position)

            // Update target2 rotation to match spotlight
            //object.target2.quaternion.copy(object.quaternion)

            object.userData.lockTarget2 = false;

            // Update texture rotation
            if (object.projectorTexture) {
              object.projectorTexture.updateTextureRotation(object.rotation);
            }

            // Update helper lines to reflect new throw distance
            if (object.projectorHelpers) {
              object.projectorHelpers.updateHelperLines();
            }
          } else {
            // Original behavior: target2 follows projector, maintaining direction and distance
            const distance = object.target.position.length() || 73;
            const direction = new THREE.Vector3(0, 0, -1);
            direction.applyQuaternion(object.quaternion);
            object.target2.position.copy(object.position).add(direction.multiplyScalar(distance));

            // Copy rotation so local transform mode aligns with spotlight
            object.target2.quaternion.copy(object.quaternion);

            // Update texture rotation
            if (object.projectorTexture) {
              object.projectorTexture.updateTextureRotation(object.rotation);
            }
          }
        } else if (object.projectorTexture) {
          // Update texture rotation even if no target2
          object.projectorTexture.updateTextureRotation(object.rotation);
        }
      }
      /*
      updateNode(object.userData.code, {
        position: {
          x: object.position.x,
          y: object.position.y,
          z: object.position.z
        }
      })
        */
      updateObject(object.userData.code, {
        data: {
          position: {
            x: object.position.x,
            y: object.position.y,
            z: object.position.z,
          },
        },
      });
    }
  });

  watch(
    () => {
      const selectedId = selectionStore.selectedObjectId;
      if (!selectedId) return null;
      const module = cruxModuleStore.cruxModules.find((m) => m.code === selectedId);
      return module ? module.data.lock_scale : null;
    },
    (newVal, oldVal) => {
      const selectedId = selectionStore.selectedObjectId;
      const mesh = getObjectByCode(selectedId);
      if (!mesh) return;
      mesh.userData.lock_scale = newVal;
      if (transformControls && transformControls.mode === 'scale') {
        changeTransformControlsAll(transformControls.mode);
      }
    }
  );

  watch([() => selectionStore.selectedObjectId], ([newObject]) => {
    if (newObject) {
      const mesh = getObjectByCode(newObject);
      if (!mesh) return;

      // Don't attach transform controls to measurements
      if (mesh.userData.type === 'measurement') {
        transformControls.detach();
        updateAllSpritesVisibility(false);
        return;
      }

      // Annotations manage their own gizmo via labelContainer click
      if (mesh.userData.type === 'annotation') {
        transformControls.detach();
        return;
      }
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }

      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      if (selectionStore.selectedObjectTransformType) {
        // Don't attach transform controls for projectors with lockTarget in rotate mode
        if (
          selectionStore.selectedObjectTransformType === 'rotate' &&
          mesh.userData.isProjector &&
          mesh.userData.lockTarget
        ) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        const scaleDisabled = selectionStore.selectedObjectTransformType === 'scale' && isScaleDisabledForMesh(mesh);
        setGizmoDisabled(scaleDisabled);

        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');

        if (selectionStore.selectedObjectTransformTarget && targetMesh) {
          if (mesh.userData.lockTarget) return;
          isTransformingTarget = true;

          transformControls.attach(targetMesh);

          changeTransformControlsAll(selectionStore.selectedObjectTransformType, true);
        } else {
          isTransformingTarget = false;
          transformControls.attach(mesh);
          // setScaleGizmoMode(mesh, transformControls)
          changeTransformControlsAll(selectionStore.selectedObjectTransformType);
        }

        const gizmo = transformControls.getHelper();
        gizmo.traverse((object) => {
          object.layers.set(1);
          if ('isMesh' in object && object.isMesh) {
            object.userData.projectorOverlayIgnore = true;
          }
        });
        scene.add(gizmo);
        updateAllSpritesVisibility(true); // Hide sprites when in transform mode
      } else {
        updateAllSpritesVisibility(false); // Show sprites when not in transform mode
      }
    } else {
      transformControls.detach();
      updateAllSpritesVisibility(false); // Show sprites when nothing is selected
    }
  });

  watch([() => selectionStore.selectedObjectTransformType], ([newType]) => {
    if (selectionStore.selectedObjectId) {
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock) return;

      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }

      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      const module = cruxModuleStore.cruxModules.find((m) => m.code === selectionStore.selectedObjectId);
      if (module) {
        mesh.userData.lock_scale = module.data.lock_scale;
      }
      const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');

      if (newType) {
        // Don't attach transform controls for projectors with lockTarget in rotate mode
        if (newType === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
          transformControls.detach();
          updateAllSpritesVisibility(false);
          return;
        }

        const scaleDisabled = newType === 'scale' && isScaleDisabledForMesh(mesh);
        setGizmoDisabled(scaleDisabled);

        if (selectionStore.selectedObjectTransformTarget && targetMesh) {
          if (mesh.userData.lockTarget) return;
          isTransformingTarget = true;

          transformControls.attach(targetMesh);
          changeTransformControlsAll(newType, true);
        } else {
          isTransformingTarget = false;
          transformControls.attach(mesh);
          // setScaleGizmoMode(mesh, transformControls)
          changeTransformControlsAll(newType);
        }

        const gizmo = transformControls.getHelper();
        gizmo.traverse((object) => {
          object.layers.set(1);
          if ('isMesh' in object && object.isMesh) {
            object.userData.projectorOverlayIgnore = true;
          }
        });
        scene.add(gizmo);
        updateAllSpritesVisibility(true); // Hide sprites when in transform mode
      } else {
        transformControls.detach();
        updateAllSpritesVisibility(false); // Show sprites when exiting transform mode
      }
    } else {
      transformControls.detach();
      updateAllSpritesVisibility(false); // Show sprites when nothing is selected
    }
    render();
  });

  watch([() => selectionStore.selectedObjectTransformTarget], ([newTargetMode]) => {
    if (selectionStore.selectedObjectId && selectionStore.selectedObjectTransformType) {
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }
      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      // Don't attach transform controls for projectors with lockTarget in rotate mode
      if (
        selectionStore.selectedObjectTransformType === 'rotate' &&
        mesh.userData.isProjector &&
        mesh.userData.lockTarget
      ) {
        transformControls.detach();
        return;
      }

      const scaleDisabled = selectionStore.selectedObjectTransformType === 'scale' && isScaleDisabledForMesh(mesh);
      setGizmoDisabled(scaleDisabled);

      // Update the internal target transform state
      isTransformingTarget = newTargetMode;

      // Re-attach with the new mode
      transformControls.detach();

      if (newTargetMode) {
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
        transformControls.attach(targetMesh);
        changeTransformControlsAll(selectionStore.selectedObjectTransformType, true);
      } else {
        transformControls.attach(mesh);
        // setScaleGizmoMode(mesh, transformControls)
        changeTransformControlsAll(selectionStore.selectedObjectTransformType, false);
      }

      const gizmo = transformControls.getHelper();
      gizmo.traverse((object) => {
        object.layers.set(1);
        if ('isMesh' in object && object.isMesh) {
          object.userData.projectorOverlayIgnore = true;
        }
      });
      scene.add(gizmo);
      render();
    }
  });
}

export function toggleTargetTransform() {
  isTransformingTarget = !isTransformingTarget;
  if (transformControls.object) {
    const selectionStore = useSelectionStore();
    const mesh = getObjectByCode(selectionStore.selectedObjectId);
    if (!mesh) return isTransformingTarget;

    transformControls.detach();
    if (isTransformingTarget && (mesh.target2 || mesh.parent.target2)) {
      transformControls.attach(mesh.target2 || mesh.parent.target2);
    } else {
      transformControls.attach(mesh);
      // setScaleGizmoMode(mesh, transformControls)
    }
  }
  return isTransformingTarget;
}

export function changeTransformControlsAll(type, target = false) {
  if (transformControls) {
    transformControls.setMode(type);
    transformControls.showX = true;
    transformControls.showY = true;
    transformControls.showZ = true;
    isTransformingTarget = target;
    if (transformControls.object) {
      const selectionStore = useSelectionStore();
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }
      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      // Don't attach transform controls for projectors with lockTarget in rotate mode
      if (type === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
        transformControls.detach();
        return;
      }

      const scaleDisabled = type === 'scale' && isScaleDisabledForMesh(mesh);
      setGizmoDisabled(scaleDisabled);

      transformControls.detach();
      if (isTransformingTarget) {
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
        if (targetMesh) {
          if (mesh.userData.lockTarget) return;
          transformControls.attach(targetMesh);
        }
      } else {
        if (mesh.userData.lock_scale && type === 'scale') {
          transformControls.attach(mesh);
          setScaleGizmoMode(mesh, transformControls);
        } else {
          transformControls.attach(mesh);
        }
      }
    }
  }
}

export function changeTransformControls(type, axis, select, target = false) {
  if (transformControls) {
    const selectionStore = useSelectionStore();
    if (select) {
      selectionStore.selectedObjectTransformType = type;
    }

    transformControls.setMode(type);
    transformControls.showX = axis === 'x';
    transformControls.showY = axis === 'y';
    transformControls.showZ = axis === 'z';

    isTransformingTarget = target;
    if (transformControls.object) {
      const mesh = getObjectByCode(selectionStore.selectedObjectId);
      if (!mesh) return;
      if (mesh.userData.lock_scale === undefined) {
        mesh.userData.lock_scale = mesh.userData.type !== 'led_screen';
      }
      if (mesh.userData.lock) {
        transformControls.detach(mesh);
        return;
      }

      // Don't attach transform controls for projectors with lockTarget in rotate mode
      if (type === 'rotate' && mesh.userData.isProjector && mesh.userData.lockTarget) {
        transformControls.detach();
        return;
      }

      const scaleDisabled = type === 'scale' && isScaleDisabledForMesh(mesh);
      setGizmoDisabled(scaleDisabled);

      transformControls.detach();
      if (isTransformingTarget) {
        const targetMesh = getObjectByCode(selectionStore.selectedObjectId + '-target');
        if (targetMesh) {
          if (mesh.userData.lockTarget) return;
          transformControls.attach(targetMesh);
        }
      } else {
        transformControls.attach(mesh);
        // setScaleGizmoMode(mesh, transformControls)
      }
    }
  }
}

export function changeTransformControlsWorld(type) {
  if (transformControls) {
    transformControls.setSpace(type);
  }
}

export function cleanupTransformControls() {
  transformControls.dispose();
}
